import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  ImmersiveScenarioCard,
  ImmersiveScenarioCardProps,
  MembotCardProps,
  MembotCard,
  ScenarioCard,
  ScenarioCardProps,
} from 'src/components';
import { useChunks } from 'src/hooks/useChunks';
import { TagPageProps } from '../types/tagPageProps';
import { msgs } from './messages';
import * as S from './styles';

type LICBlockProps = {
  className?: string;
  description: string;
  title: string;
} & (
  | {
      immersiveScenarios?: undefined;
      membotMissions: TagPageProps['membotMissions'];
      scenarios?: undefined;
      type: 'communicate';
    }
  | {
      immersiveScenarios: TagPageProps['immersiveScenarios'];
      membotMissions?: undefined;
      scenarios?: undefined;
      type: 'immerse';
    }
  | {
      immersiveScenarios?: undefined;
      membotMissions?: undefined;
      scenarios: TagPageProps['scenarios'];
      type: 'learn';
    }
);

export const LICBlock = ({
  className,
  description,
  immersiveScenarios,
  membotMissions,
  scenarios,
  title,
  type,
}: LICBlockProps) => {
  const intl = useIntl();

  const { asPath } = useRouter();

  const renderMembotMission = useCallback(
    (mission: MembotCardProps['mission']) => (
      <MembotCard
        key={mission.locationSlug}
        backLink={asPath}
        linkLabel={intl.formatMessage(msgs.practiceSpeaking)}
        mission={mission}
      />
    ),
    [asPath, intl],
  );

  const renderImmersiveScenario = useCallback(
    (scenario: ImmersiveScenarioCardProps['scenario']) => (
      <ImmersiveScenarioCard
        key={scenario.scenarioId}
        linkLabel={intl.formatMessage(msgs.watchTheVideo)}
        scenario={scenario}
      />
    ),
    [intl],
  );

  const renderScenario = useCallback(
    (scenario: ScenarioCardProps['scenario']) => (
      <ScenarioCard key={scenario.scenarioId} scenario={scenario} />
    ),
    [],
  );

  const immersiveScenariosChunks = useChunks(immersiveScenarios || [], S.CHUNK_SIZE);
  const membotMissionsChunks = useChunks(membotMissions || [], S.CHUNK_SIZE);
  const scenariosChunks = useChunks(scenarios || [], S.CHUNK_SIZE);

  return (
    <S.LICBlock className={className} $type={type}>
      <S.Content>
        <S.Title>{title}</S.Title>
        <S.Description>{description}</S.Description>

        {type === 'communicate' && membotMissions && (
          <>
            <S.ReactResponsiveCarousel>
              {membotMissionsChunks.map(membotMissionsChunk => (
                <S.Chunk key={membotMissionsChunk[0].locationSlug}>
                  {membotMissionsChunk.map(renderMembotMission)}
                </S.Chunk>
              ))}
            </S.ReactResponsiveCarousel>
            <S.Scroll>{membotMissions.map(renderMembotMission)}</S.Scroll>
          </>
        )}

        {type === 'immerse' && immersiveScenarios && (
          <>
            <S.ReactResponsiveCarousel>
              {immersiveScenariosChunks.map(immersiveScenariosChunk => (
                <S.Chunk key={immersiveScenariosChunk[0].scenarioId}>
                  {immersiveScenariosChunk.map(renderImmersiveScenario)}
                </S.Chunk>
              ))}
            </S.ReactResponsiveCarousel>
            <S.Scroll>{immersiveScenarios.map(renderImmersiveScenario)}</S.Scroll>
          </>
        )}

        {type === 'learn' && scenarios && (
          <>
            <S.ReactResponsiveCarousel>
              {scenariosChunks.map(scenariosChunk => (
                <S.Chunk key={scenariosChunk[0].scenarioId}>
                  {scenariosChunk.map(renderScenario)}
                </S.Chunk>
              ))}
            </S.ReactResponsiveCarousel>
            <S.Scroll>{scenarios.map(renderScenario)}</S.Scroll>
          </>
        )}
      </S.Content>
    </S.LICBlock>
  );
};
